import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, finalize, map } from 'rxjs';

import {
  Conversation,
  CreateBulkMessage,
  CreateMessage,
  Message,
  MessageEntityType,
  PaginatedList,
} from '@shared/models';

import { BaseService } from '../base.service';
import { httpParams } from '../http-params.builder';
import { LoadersService } from '../loaders.service';
import { Filter, Sort } from '../table-filters.service';

@Injectable({
  providedIn: 'root',
})
export class ChatService extends BaseService {
  constructor(
    private http: HttpClient,
    private loadersService: LoadersService
  ) {
    super();
  }

  get(
    loader: string,
    pageIndex = 0,
    pageSize = 50,
    entityType?: MessageEntityType,
    filters: Filter = null,
    sort: Sort = null,
    searchQuery?: string,
    unreadOnly = false
  ): Observable<PaginatedList<Conversation>> {
    this.loadersService.show(loader);
    return this.http
      .get<PaginatedList<Conversation>>(`${this.baseUrl}/conversations`, {
        params: httpParams()
          .paging(pageIndex, pageSize)
          .table(filters, sort)
          .set('entityType', entityType)
          .set('searchQuery', searchQuery)
          .set('unreadOnly', unreadOnly || undefined)
          .build(),
      })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  getById(loader: string, id: string): Observable<Conversation> {
    this.loadersService.show(loader);
    return this.http
      .get<Conversation>(`${this.baseUrl}/conversations/${id}`)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  getMessages(
    loader: string,
    entityType: MessageEntityType,
    entityId: string,
    pageIndex = 0,
    pageSize = 50,
    filters: Filter = null,
    sort: Sort = null
  ): Observable<PaginatedList<Message>> {
    this.loadersService.show(loader);
    return this.http
      .get<PaginatedList<Message>>(`${this.baseUrl}/conversations/${entityType}/${entityId}/messages`, {
        params: httpParams().paging(pageIndex, pageSize).table(filters, sort).build(),
      })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  getMessagesCount(loader: string, entityType: MessageEntityType, entityId: string): Observable<number> {
    this.loadersService.show(loader);
    return this.http
      .get<number>(`${this.baseUrl}/conversations/${entityType}/${entityId}/messages/count`)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  getUnReadMessagesCount(loader: string): Observable<number> {
    this.loadersService.show(loader);
    return this.http
      .get<number>(`${this.baseUrl}/conversations/messages/count/unread`)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  markConversationMessagesRead(loader: string, entityType: MessageEntityType, entityId: string): Observable<boolean> {
    this.loadersService.show(loader);
    return this.http
      .post(`${this.baseUrl}/conversations/${entityType}/${entityId}/messages/read`, {}, { observe: 'response' })
      .pipe(
        map((response) => this.isStatusSucceed(response.status)),
        finalize(() => this.loadersService.hide(loader))
      );
  }

  add(loader: string, dto: CreateMessage): Observable<Message> {
    this.loadersService.show(loader);
    return this.http
      .post<Message>(this.baseUrl + '/conversations/messages', dto)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  addBulkMessage(loader: string, dto: CreateBulkMessage): Observable<string[]> {
    this.loadersService.show(loader);
    return this.http
      .post<string[]>(this.baseUrl + '/conversations/messages/bulk', dto)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  update(loader: string, dto: Message): Observable<Message> {
    this.loadersService.show(loader);
    return this.http
      .put<Message>(this.baseUrl + '/conversations/messages', dto)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  delete(loader: string, id: string): Observable<boolean> {
    this.loadersService.show(loader);
    return this.http.delete(`${this.baseUrl}/conversations/messages/${id}`, { observe: 'response' }).pipe(
      map((response) => this.isStatusSucceed(response.status)),
      finalize(() => this.loadersService.hide(loader))
    );
  }
}
